import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { ApplicationService } from "../../services/ApplicationService";
import { AgentActionService } from "../../services/AgentActionService";
import { Application } from "../../model_gen/application";
import { AgentAction, Agent } from "../../model_gen/agent_base";
import { AgentService } from "../../services/AgentService";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { ArrowLeftIcon, EditIcon, ExternalLinkIcon, EyeIcon, EyeOffIcon, InfoIcon, PlayIcon, X, Loader2, SettingsIcon, UserIcon, HistoryIcon } from "lucide-react";
import { AlertCircle } from "lucide-react";
import { Tooltip } from "../ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { APIActionConfig, LLMActionConfig } from "../../types/AgentAction";
import {
  isSensitiveHeader,
  getHeaderDisplayValue,
  useSensitiveHeaderVisibility,
} from "../../utils/headerSecurity";
import PromptModal from "../ui/PromptModal";
import { VersionBadge } from "./VersionBadge";
import { cn } from "../../lib/utils";
import AgentActionVersionsTable from "./AgentActionVersionsTable";

interface TabItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const AgentActionOverview: React.FC = () => {
  const { agentId, actionId } = useParams<{
    agentId: string;
    actionId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version") || undefined;
  const { apiKey } = usePermissions();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [action, setAction] = useState<AgentAction | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const isVersionView = !!version;
  
  // Version state
  const [latestVersion, setLatestVersion] = useState<string | undefined>(undefined);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  
  // Prompt modal state
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);

  // Test modal state
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testParams, setTestParams] = useState<Record<string, any>>({});
  const [testRequestBody, setTestRequestBody] = useState<string>("");
  const [testResponse, setTestResponse] = useState<{
    status?: number;
    statusText?: string;
    body?: any;
    error?: string;
    loading?: boolean;
    responseTime?: number;
    responseSize?: number;
  }>({});
  const [isTestingAction, setIsTestingAction] = useState(false);
  
  // Publish modal state
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishIncrement, setPublishIncrement] = useState<"major" | "minor" | "patch">("patch");
  const [publishNotes, setPublishNotes] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDraftingFromVersion, setIsDraftingFromVersion] = useState(false);
  const [draftConfirmDialogOpen, setDraftConfirmDialogOpen] = useState<boolean>(false);
  const [pendingDraftVersion, setPendingDraftVersion] = useState<string | null>(null);
  
  // Helper: Reload version status (for draft view only)
  const reloadVersionStatus = useCallback(() => {
    if (!actionId || isVersionView) return;
    AgentActionService.getActionVersionStatus(Number(actionId), () => {}, apiKey).subscribe(
      (versionStatus: any) => {
        setLatestVersion(versionStatus.latest_version);
        setHasUnpublishedChanges(!!versionStatus.has_unpublished_changes);
        // Dispatch event for child components
        window.dispatchEvent(new CustomEvent("actionVersionStatusUpdated", {
          detail: { actionId: Number(actionId), versionStatus }
        }));
      },
      (error: any) => console.error("Error loading action version status:", error)
    );
  }, [actionId, apiKey, isVersionView]);

  // Helper: Draft from version
  const handleDraftFromVersion = useCallback(async (versionToDraft: string) => {
    if (!actionId || !versionToDraft || isDraftingFromVersion) return;
    
    // Open confirmation dialog
    setPendingDraftVersion(versionToDraft);
    setDraftConfirmDialogOpen(true);
  }, [actionId, isDraftingFromVersion]);

  // Confirm and execute draft from version
  const confirmDraftFromVersion = useCallback(() => {
    if (!actionId || !pendingDraftVersion || isDraftingFromVersion) return;
    
    setDraftConfirmDialogOpen(false);
    setIsDraftingFromVersion(true); // Immediate feedback - show loading state
    
    // Create draft FIRST, then navigate after it's ready
    AgentActionService.draftFromActionVersion(
      Number(actionId),
      pendingDraftVersion,
      () => {},
      apiKey
    ).subscribe(
      () => {
        // Draft created successfully - backend already waited for hash computation
        // Now navigate to draft view (if we were viewing a version)
        if (isVersionView && version) {
          const params = new URLSearchParams(searchParams);
          params.delete("version");
          navigate(`/agent/${agentId}/actions/${actionId}?${params.toString()}`);
        }
        
        // Reload version status and action data immediately
        // Backend already returned refreshed status, so we can reload right away
          reloadVersionStatus();
          AgentActionService.getAction(Number(actionId), () => {}, apiKey).subscribe(
            (updatedAction: AgentAction) => {
              setAction(updatedAction);
              setIsLoading(false);
              setIsDraftingFromVersion(false);
              setPendingDraftVersion(null);
            },
            (err: any) => {
              console.error("Error reloading action:", err);
              setIsDraftingFromVersion(false);
              setPendingDraftVersion(null);
            }
          );
      },
      (err: any) => {
        console.error("Error drafting from version:", err);
        setIsDraftingFromVersion(false);
        setPendingDraftVersion(null);
      }
    );
  }, [actionId, agentId, apiKey, isVersionView, version, searchParams, navigate, reloadVersionStatus, isDraftingFromVersion, pendingDraftVersion]);

  // Helper: Ensure draft exists before editing (if viewing version)
  const ensureDraftBeforeEdit = useCallback(async (onDraftReady: () => void) => {
    if (isVersionView && version) {
      // Draft from version first
      setIsDraftingFromVersion(true); // Immediate feedback - show loading state
      AgentActionService.draftFromActionVersion(
        Number(actionId),
        version,
        () => {},
        apiKey
      ).subscribe(
        () => {
          // Draft created successfully - backend already waited for hash computation
          // Navigate to draft view
          navigate(`/agent/${agentId}/actions/${actionId}`);
          
          // Reload version status and action data immediately, then execute edit action
          // Backend already returned refreshed status, so we can reload right away
            reloadVersionStatus();
            AgentActionService.getAction(Number(actionId), () => {}, apiKey).subscribe(
              (updatedAction: AgentAction) => {
                setAction(updatedAction);
                setIsLoading(false);
                setIsDraftingFromVersion(false);
                // Execute the edit action after draft is ready
                  onDraftReady();
              },
              (err: any) => {
                console.error("Error reloading action:", err);
                setIsDraftingFromVersion(false);
              }
            );
        },
        (err: any) => {
          console.error("Error drafting from version:", err);
          setIsDraftingFromVersion(false);
        }
      );
    } else {
      // Already in draft view, proceed with edit
      onDraftReady();
    }
  }, [actionId, agentId, apiKey, isVersionView, version, navigate, reloadVersionStatus]);
  
  // Use the centralized hook for managing sensitive header visibility
  const { visibleHeaders, toggleHeaderVisibility, isHeaderVisible } =
    useSensitiveHeaderVisibility();

  // Convert OpenAPI format back to UI-friendly format for display
  const convertFromOpenAPIFormat = (openAPIConfig: any): any => {
    if (!openAPIConfig || typeof openAPIConfig !== "object") {
      return {};
    }

    // For LLM configs, return as-is (they don't use OpenAPI format)
    if (
      openAPIConfig.model_config ||
      openAPIConfig.prompt ||
      (openAPIConfig.endpoint === undefined &&
        openAPIConfig.openapi === undefined)
    ) {
      return openAPIConfig;
    }

    // If it's already in UI format (has endpoint, method, etc. directly), return as-is
    if (
      openAPIConfig.endpoint ||
      openAPIConfig.method ||
      openAPIConfig.headers
    ) {
      return openAPIConfig;
    }

    // If it's an OpenAPI spec, parse it
    if (openAPIConfig.openapi || openAPIConfig.swagger) {
      const uiConfig: any = {
        endpoint: "",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        timeout: 30,
        parameters: [],
        requestSchema: {},
        responseSchemas: {},
        base_url: "",
        auth: {
          type: "none",
          token: "",
          header: "Authorization",
          key: "",
        },
      };

      // Extract endpoint and method from paths
      if (openAPIConfig.paths && typeof openAPIConfig.paths === "object") {
        const pathEntries = Object.entries(openAPIConfig.paths);
        if (pathEntries.length > 0) {
          const [path, pathItem] = pathEntries[0] as [string, any];

          // Reconstruct full URL
          if (openAPIConfig.servers && openAPIConfig.servers[0]?.url) {
            uiConfig.endpoint = openAPIConfig.servers[0].url + path;
          } else {
            uiConfig.endpoint = path;
          }

          // Get the first HTTP method
          const methods = ["get", "post", "put", "patch", "delete"];
          for (const method of methods) {
            if (pathItem[method]) {
              uiConfig.method = method.toUpperCase();
              const operation = pathItem[method];

              // Extract parameters
              if (operation.parameters && Array.isArray(operation.parameters)) {
                uiConfig.parameters = operation.parameters
                  .filter(
                    (param: any) => param.in === "path" || param.in === "query"
                  )
                  .map((param: any) => ({
                    name: param.name,
                    in: param.in,
                    type: param.schema?.type || "string",
                    required: param.required || false,
                    description: param.description,
                    defaultValue: param.schema?.default,
                    enum: param.schema?.enum,
                  }));
              }

              // Extract request body schema
              if (
                operation.requestBody?.content?.["application/json"]?.schema
              ) {
                uiConfig.requestSchema = convertOpenAPISchemaToUIFormat(
                  operation.requestBody.content["application/json"].schema
                );
              }

              // Extract response schemas
              if (
                operation.responses &&
                typeof operation.responses === "object"
              ) {
                Object.entries(operation.responses).forEach(
                  ([statusCode, response]: [string, any]) => {
                    if (response.content?.["application/json"]?.schema) {
                      uiConfig.responseSchemas[statusCode] =
                        convertOpenAPISchemaToUIFormat(
                          response.content["application/json"].schema
                        );
                    }
                  }
                );
              }

              break;
            }
          }
        }
      }

      // Extract authentication
      if (openAPIConfig.components?.securitySchemes) {
        const securitySchemes = openAPIConfig.components.securitySchemes;
        if (
          securitySchemes.bearerAuth?.type === "http" &&
          securitySchemes.bearerAuth?.scheme === "bearer"
        ) {
          uiConfig.auth.type = "bearer";
        } else if (securitySchemes.apiKeyAuth?.type === "apiKey") {
          uiConfig.auth.type = "api_key";
          uiConfig.auth.header = securitySchemes.apiKeyAuth.name;
        }
      }

      // Extract base_url if present
      if (openAPIConfig.base_url) {
        uiConfig.base_url = openAPIConfig.base_url;
      }

      // Extract implied_base_url if present
      if (openAPIConfig.implied_base_url) {
        uiConfig.implied_base_url = openAPIConfig.implied_base_url;
      }

      // Extract response_type if present
      if (openAPIConfig.response_type) {
        uiConfig.response_type = openAPIConfig.response_type;
      }

      // Extract mock_responses if present
      if (openAPIConfig.mock_responses) {
        uiConfig.mock_responses = openAPIConfig.mock_responses;
      }

      return uiConfig;
    }

    // Return as-is if we can't parse it
    return openAPIConfig;
  };

  // Convert OpenAPI schema to UI SchemaProperty format
  const convertOpenAPISchemaToUIFormat = (
    schema: any
  ): { [key: string]: any } => {
    if (!schema?.properties) {
      return {};
    }

    const uiProperties: { [key: string]: any } = {};

    Object.entries(schema.properties).forEach(
      ([propName, propSchema]: [string, any]) => {
        const uiProp: any = {
          name: propName,
          type: propSchema.type || "string",
          description: propSchema.description || "",
          required: schema.required?.includes(propName) || false,
        };

        if (propSchema.format) uiProp.format = propSchema.format;
        if (propSchema.enum) uiProp.enum = propSchema.enum;

        if (propSchema.type === "object" && propSchema.properties) {
          uiProp.properties = convertOpenAPISchemaToUIFormat(propSchema);
        } else if (propSchema.type === "array" && propSchema.items) {
          uiProp.items = {
            name: "item",
            type: propSchema.items.type || "string",
            description: propSchema.items.description || "",
          };
          if (propSchema.items.properties) {
            uiProp.items.properties = convertOpenAPISchemaToUIFormat(
              propSchema.items
            );
          }
        }

        uiProperties[propName] = uiProp;
      }
    );

    return uiProperties;
  };

  // Load agent, application, and action details
  useEffect(() => {
    let agentSubscription: any = null;
    let applicationSubscription: any = null;
    let actionSubscription: any = null;
    let versionSubscription: any = null;

    if (agentId && actionId && apiKey) {
      setIsLoading(true);
      setError(null);

      // Load agent details
      const agentSubscription = AgentService.getAgent(
        Number(agentId),
        () => {},
        apiKey
      ).subscribe(
        (agent: Agent) => {
          setAgent(agent);

          // Load application details
          if (agent.application_id) {
            const appSubscription = ApplicationService.getApplication(
              agent.application_id,
              () => {},
              apiKey
            ).subscribe(
              (app: Application) => {
                setApplication(app);
              },
              (error: any) => {
                console.error("Error loading application:", error);
                setError("Failed to load application details");
              }
            );
          }

          // Load action details

          if (isVersionView && version) {
            // Load versioned action
            actionSubscription = AgentActionService.getActionVersion(
              Number(actionId),
              version,
              () => {},
              apiKey
            ).subscribe(
              (actionVersion: any) => {
                // Convert AgentActionVersion to AgentAction format for display
                const versionedAction: AgentAction = {
                  id: actionVersion.agent_action_id,
                  date_created: actionVersion.date_created,
                  agent_id: 0, // Will be set from agent
                  action_name: actionVersion.action_name,
                  action_type: actionVersion.action_type,
                  description: actionVersion.description,
                  action_config: actionVersion.action_config,
                  action_result_type: actionVersion.action_result_type,
                  invocation_mode: actionVersion.invocation_mode,
                  meta: actionVersion.meta,
                  content_hash: actionVersion.content_hash,
                };
                setAction(versionedAction);
                setIsLoading(false);
              },
              (error: any) => {
                console.error("Error loading action version:", error);
                setError("Failed to load action version");
                setIsLoading(false);
              }
            );
          } else {


           actionSubscription = AgentActionService.getAction(
            Number(actionId),
            () => {},
            apiKey
          ).subscribe(
            (action: AgentAction) => {
              setAction(action);
              setIsLoading(false);
            },
            (error: any) => {
              console.error("Error loading action:", error);
              setError("Failed to load action details");
              setIsLoading(false);
              }
            );
          }

          // Load version status (only for draft view)
          if (!isVersionView) {
            reloadVersionStatus();
          }
        },
        (error: any) => {
          console.error("Error loading agent:", error);
          setError("Failed to load agent details");
          setIsLoading(false);
        }
      );
    }

    // Cleanup subscriptions on effect cleanup or component unmount
    return () => {
      if (agentSubscription) {
        agentSubscription.unsubscribe();
      }
      if (applicationSubscription) {
        applicationSubscription.unsubscribe();
      }
      if (actionSubscription) {
        actionSubscription.unsubscribe();
      }
      if (versionSubscription) {
        versionSubscription.unsubscribe();
      }
    };
  }, [agentId, actionId, apiKey, isVersionView, version]);

  // Listen for version status updates from child components (e.g., AgentActionVersionsTable)
  useEffect(() => {
    const handleVersionStatusUpdate = (event: any) => {
      const { actionId: eventActionId, versionStatus } = event.detail || {};
      if (eventActionId && Number(eventActionId) === Number(actionId)) {
        setLatestVersion(versionStatus?.latest_version);
        setHasUnpublishedChanges(!!versionStatus?.has_unpublished_changes);
      }
    };

    window.addEventListener("actionVersionStatusUpdated", handleVersionStatusUpdate);
    return () => {
      window.removeEventListener("actionVersionStatusUpdated", handleVersionStatusUpdate);
    };
  }, [actionId]);

  const handleEdit = () => {
    ensureDraftBeforeEdit(() => {
      navigate(`/agent/${agentId}/actions/${actionId}/edit`);
    });
  };

  const handleCancel = () => {
    navigate(`/agent/${agentId}`);
  };

  const handleTestAction = async () => {
    if (!action || !actionId || isTestingAction) return;

    setIsTestingAction(true);
    setTestResponse({ loading: true });

    try {
      const config = convertFromOpenAPIFormat(action?.action_config || {}) as APIActionConfig;
      
      // Check if we should return a mock response (mimic actions_http.py logic)
      const responseType = config.response_type || "real";
      const mockResponses = config.mock_responses || {};

      if (
        responseType === "mock" &&
        mockResponses &&
        Object.keys(mockResponses).length > 0
      ) {
        // Return mock response for 200 status if available, otherwise first available mock
        const mockResponse =
          mockResponses["200"] || Object.values(mockResponses)[0];

        // Simulate a brief delay to make it feel like a real API call
        await new Promise((resolve) => setTimeout(resolve, 500));

        setTestResponse({
          status: 200,
          statusText: "OK (Mock Response)",
          body: mockResponse,
          loading: false,
        });

        return;
      }

      // Build the actual API call using the action's configuration
      const baseUrl = config.base_url || (config as any).implied_base_url;
      const endpoint = config.endpoint || "";

      // Construct the full URL
      let fullUrl: string;
      if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
        // Full URL provided
        fullUrl = endpoint;
      } else if (baseUrl) {
        // Relative path with base URL
        const cleanBase = baseUrl.replace(/\/$/, "");
        const cleanPath = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
        fullUrl = `${cleanBase}${cleanPath}`;
      } else {
        throw new Error("No base URL available and endpoint is not a full URL");
      }

      // Replace path parameters in the URL
      let processedUrl = fullUrl;
      if (config.parameters) {
        config.parameters.forEach((param) => {
          if (param.in === "path") {
            const paramValue = testParams[param.name];
            if (paramValue !== undefined) {
              processedUrl = processedUrl.replace(
                `{${param.name}}`,
                encodeURIComponent(String(paramValue))
              );
            }
          }
        });
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (config.parameters) {
        config.parameters.forEach((param) => {
          if (param.in === "query") {
            const paramValue = testParams[param.name];
            if (paramValue !== undefined && paramValue !== "") {
              queryParams.append(param.name, String(paramValue));
            }
          }
        });
      }

      // Add query parameters to URL
      if (queryParams.toString()) {
        processedUrl +=
          (processedUrl.includes("?") ? "&" : "?") + queryParams.toString();
      }

      // Build headers
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...config.headers,
      };

      // Add auth headers
      if (config.auth && config.auth.type !== "none") {
        if (config.auth.type === "bearer" && config.auth.token) {
          headers["Authorization"] = `Bearer ${config.auth.token}`;
        } else if (
          config.auth.type === "api_key" &&
          config.auth.key &&
          config.auth.header
        ) {
          headers[config.auth.header] = config.auth.key;
        }
      }

      // Build request body
      let requestBody = undefined;
      if (
        config.method &&
        ["POST", "PUT", "PATCH"].includes(config.method) &&
        testRequestBody
      ) {
        try {
          requestBody = JSON.parse(testRequestBody);
        } catch (e) {
          throw new Error("Invalid JSON in request body");
        }
      }

      // Make the actual API call with timing
      const startTime = performance.now();
      const response = await fetch(processedUrl, {
        method: config.method || "POST",
        headers,
        body: requestBody ? JSON.stringify(requestBody) : undefined,
      });
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      let responseBody;
      let responseText;
      try {
        responseText = await response.text();
        responseBody = responseText ? JSON.parse(responseText) : null;
      } catch (e) {
        // Handle non-JSON responses
        responseBody = responseText;
      }

      // Calculate response size in bytes
      const responseSize = new TextEncoder().encode(responseText || "").length;

      setTestResponse({
        status: response.status,
        statusText: response.statusText,
        body: responseBody,
        responseTime,
        responseSize,
        loading: false,
      });
    } catch (error) {
      setTestResponse({
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
        loading: false,
      });
    } finally {
      setIsTestingAction(false);
    }
  };

  const initializeTestParams = (config: APIActionConfig) => {
    if (!config.parameters) return;

    const initialParams: Record<string, any> = {};
    config.parameters.forEach((param) => {
      if (param.defaultValue !== undefined) {
        initialParams[param.name] = param.defaultValue;
      } else {
        switch (param.type) {
          case "string":
            initialParams[param.name] = "";
            break;
          case "number":
          case "integer":
            initialParams[param.name] = 0;
            break;
          case "boolean":
            initialParams[param.name] = false;
            break;
          default:
            initialParams[param.name] = "";
        }
      }
    });
    setTestParams(initialParams);

    // Initialize request body if schema exists
    if (config.requestSchema && Object.keys(config.requestSchema).length > 0) {
      try {
        const example = generateExampleFromSchema(config.requestSchema);
        setTestRequestBody(JSON.stringify(example, null, 2));
      } catch (_) {
        setTestRequestBody("{}");
      }
    } else {
      setTestRequestBody("");
    }
  };

  const generateExampleFromSchema = (schema: { [key: string]: any }): any => {
    const result: any = {};
    Object.entries(schema || {}).forEach(([key, prop]) => {
      if (!prop) return;
      switch (prop.type) {
        case "string":
          result[key] =
            prop.enum && prop.enum.length > 0 ? prop.enum[0] : "example";
          break;
        case "number":
        case "integer":
          result[key] = 1;
          break;
        case "boolean":
          result[key] = true;
          break;
        case "array":
          result[key] = [];
          break;
        case "object":
          result[key] = generateExampleFromSchema(prop.properties || {});
          break;
        default:
          result[key] = null;
      }
    });
    return result;
  };

  const validateTestParams = (config: APIActionConfig): string[] => {
    const errors: string[] = [];
    if (!config.parameters) return errors;

    config.parameters.forEach((param) => {
      if (
        param.required &&
        (!testParams[param.name] || testParams[param.name] === "")
      ) {
        errors.push(`${param.name} is required`);
      }
    });

    if (testRequestBody && testRequestBody.trim()) {
      try {
        JSON.parse(testRequestBody);
      } catch (_) {
        errors.push("Request body must be valid JSON");
      }
    }

    return errors;
  };

  const handlePromptSave = (value: string) => {
    if (!action || !actionId || promptSaving) return;

    setPromptSaving(true);

    // Update the action config with the new prompt
    const updatedActionConfig = {
      ...action.action_config,
      prompt: value,
    };

    // Save the updated action
    AgentActionService.updateAction(
      Number(actionId),
      {
        action_name: action.action_name,
        description: action.description,
        action_config: updatedActionConfig,
        action_type: action.action_type,
        invocation_mode: (action as any).invocation_mode,
        meta: (action as any).meta,
      },
      () => {},
      apiKey
    ).subscribe(
      (updatedAction: AgentAction) => {
        // Update the local action state with the saved changes
        setAction(updatedAction);
        setPromptSaving(false);
        setPromptModalOpen(false);
        
        // Reload version status to update hasUnpublishedChanges
        reloadVersionStatus();
      },
      (error: any) => {
        console.error("Error saving prompt:", error);
        setPromptSaving(false);
        // Keep the modal open on error so user can try again
      }
    );
  };

  const handlePublish = () => {
    if (!actionId || isPublishing) return;

    setIsPublishing(true);

    AgentActionService.publishActionVersion(
      Number(actionId),
      publishIncrement,
      publishNotes || undefined,
      () => {},
      apiKey
    ).subscribe(
      (result: any) => {
        // Reload version status after publishing
        reloadVersionStatus();
        setIsPublishing(false);
        setPublishModalOpen(false);
        setPublishNotes("");
        setPublishIncrement("patch");
      },
      (error: any) => {
        console.error("Error publishing version:", error);
        setIsPublishing(false);
        // Keep the modal open on error so user can try again
      }
    );
  };

  const renderAPIConfig = (config: APIActionConfig) => {
    const methodColors: { [key: string]: string } = {
      GET: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
      POST: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
      PUT: "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300",
      PATCH:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
      DELETE: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300",
    };

    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-gc-med text-foreground">
              API Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Endpoint and Method */}
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {config.method && (
                    <Badge
                      className={`font-mono ${
                        methodColors[config.method] ||
                        "bg-gray-100 text-gray-800"
                      }`}>
                      {config.method}
                    </Badge>
                  )}
                  <span className="font-mono text-sm break-all">
                    {config.endpoint || "No endpoint configured"}
                  </span>
                </div>

                {/* Full resolved URL if we have implied base URL */}
                {(config as any).implied_base_url && config.endpoint && (
                  <div className="pl-4 border-l-2 border-muted">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        Resolved URL:
                      </span>
                      <span className="font-mono text-sm break-all text-maitai-green">
                        {(config as any).implied_base_url.replace(/\/$/, "")}/{config.endpoint.replace(/^\//, "")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Base URL Information */}
            {(config.base_url || (config as any).implied_base_url) && (
              <div className="space-y-2">
                <h4 className="text-sm font-gc-med text-foreground">
                  Base URL
                </h4>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm break-all">
                      {config.base_url || (config as any).implied_base_url}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {config.base_url
                      ? "This action uses a custom base URL instead of the agent's default base URL."
                      : "This action inherits the base URL from the agent configuration."}
                  </p>
                </div>
              </div>
            )}

            {/* Authentication */}
            {config.auth && config.auth.type !== "none" && (
              <div className="space-y-2">
                <h4 className="text-sm font-gc-med text-foreground">
                  Authentication
                </h4>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{config.auth.type}</Badge>
                    {config.auth.header && (
                      <span className="text-sm text-muted-foreground">
                        Header:{" "}
                        <span className="font-mono">{config.auth.header}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Headers */}
            {config.headers && Object.keys(config.headers).length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-gc-med text-foreground">Headers</h4>
                <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                  {Object.entries(config.headers).map(([key, value]) => {
                    const isSensitive = isSensitiveHeader(key);
                    const isVisible = isHeaderVisible(key);
                    const displayValue = getHeaderDisplayValue(
                      key,
                      String(value),
                      isVisible
                    );

                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-muted-foreground">
                          {key}:
                        </span>
                        <span className="font-mono">{displayValue}</span>
                        {isSensitive && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => toggleHeaderVisibility(key)}>
                            {isVisible ? (
                              <EyeOffIcon className="w-3 h-3" />
                            ) : (
                              <EyeIcon className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Parameters */}
            {config.parameters && config.parameters.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-gc-med text-foreground">
                  Parameters
                </h4>
                <div className="space-y-2">
                  {config.parameters.map((param, index) => (
                    <div key={index} className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm">{param.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {param.in}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {param.type}
                        </Badge>
                        {param.required && (
                          <Badge variant="destructive" className="text-xs">
                            required
                          </Badge>
                        )}
                      </div>
                      {param.description && (
                        <p className="text-xs text-muted-foreground">
                          {param.description}
                        </p>
                      )}
                      {param.defaultValue !== undefined && (
                        <p className="text-xs text-muted-foreground">
                          Default:{" "}
                          <span className="font-mono">
                            {String(param.defaultValue)}
                          </span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Request Schema */}
            {config.requestSchema &&
              Object.keys(config.requestSchema).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-gc-med text-foreground">
                    Request Schema
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    {Object.entries(config.requestSchema).map(
                      ([propName, propSchema]: [string, any]) => (
                        <div
                          key={propName}
                          className="border-l-2 border-muted pl-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm">
                              {propName}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {propSchema.type || "string"}
                            </Badge>
                            {propSchema.required && (
                              <Badge variant="destructive" className="text-xs">
                                required
                              </Badge>
                            )}
                          </div>
                          {propSchema.description && (
                            <p className="text-xs text-muted-foreground">
                              {propSchema.description}
                            </p>
                          )}
                          {propSchema.enum && (
                            <p className="text-xs text-muted-foreground">
                              Enum:{" "}
                              <span className="font-mono">
                                {propSchema.enum.join(", ")}
                              </span>
                            </p>
                          )}
                          {propSchema.format && (
                            <p className="text-xs text-muted-foreground">
                              Format:{" "}
                              <span className="font-mono">
                                {propSchema.format}
                              </span>
                            </p>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Response Schemas */}
            {config.responseSchemas &&
              Object.keys(config.responseSchemas).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-gc-med text-foreground">
                    Response Schemas
                  </h4>
                  <div className="space-y-3">
                    {Object.entries(config.responseSchemas).map(
                      ([statusCode, schema]: [string, any]) => (
                        <div
                          key={statusCode}
                          className="p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge
                              className={`font-mono ${
                                statusCode.startsWith("2")
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                                  : statusCode.startsWith("4")
                                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
                                  : statusCode.startsWith("5")
                                  ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                              }`}>
                              {statusCode}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Response Schema
                            </span>
                          </div>
                          {schema && Object.keys(schema).length > 0 ? (
                            <div className="space-y-2">
                              {Object.entries(schema).map(
                                ([propName, propSchema]: [string, any]) => (
                                  <div
                                    key={propName}
                                    className="border-l-2 border-muted pl-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-mono text-sm">
                                        {propName}
                                      </span>
                                      <Badge
                                        variant="outline"
                                        className="text-xs">
                                        {propSchema.type || "string"}
                                      </Badge>
                                      {propSchema.required && (
                                        <Badge
                                          variant="destructive"
                                          className="text-xs">
                                          required
                                        </Badge>
                                      )}
                                    </div>
                                    {propSchema.description && (
                                      <p className="text-xs text-muted-foreground">
                                        {propSchema.description}
                                      </p>
                                    )}
                                    {propSchema.enum && (
                                      <p className="text-xs text-muted-foreground">
                                        Enum:{" "}
                                        <span className="font-mono">
                                          {propSchema.enum.join(", ")}
                                        </span>
                                      </p>
                                    )}
                                    {propSchema.format && (
                                      <p className="text-xs text-muted-foreground">
                                        Format:{" "}
                                        <span className="font-mono">
                                          {propSchema.format}
                                        </span>
                                      </p>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              No schema defined
                            </p>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Timeout */}
            {config.timeout && (
              <div className="space-y-2">
                <h4 className="text-sm font-gc-med text-foreground">Timeout</h4>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">{config.timeout} seconds</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderLLMConfig = (config: LLMActionConfig) => {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-gc-med text-foreground">
              LLM Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Model Configuration */}
            {config.model_config && (
              <div className="space-y-2">
                <h4 className="text-sm font-gc-med text-foreground">
                  Model Configuration
                </h4>
                <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                  {config.model_config.model && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Model:
                      </span>
                      <span className="font-mono text-sm">
                        {config.model_config.model}
                      </span>
                    </div>
                  )}
                  {config.model_config.temperature !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Temperature:
                      </span>
                      <span className="font-mono text-sm">
                        {config.model_config.temperature}
                      </span>
                    </div>
                  )}
                  {config.model_config.streaming !== undefined && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Streaming:
                      </span>
                      <Badge variant="outline">
                        {config.model_config.streaming ? "true" : "false"}
                      </Badge>
                    </div>
                  )}
                  {config.model_config.response_format && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        Response Format:
                      </span>
                      <span className="font-mono text-sm">
                        {config.model_config.response_format}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Prompt */}
            {config.prompt && (
              <div className="space-y-2">
                <h4 className="text-sm font-gc-med text-foreground">
                  Prompt Template
                </h4>
                <Button
                  onClick={() => setPromptModalOpen(true)}
                  variant="outline"
                  className="w-full h-24 justify-start items-start text-left p-4 font-normal">
                  <div className="w-full">
                    <div className="space-y-2">
                      <div className="text-sm text-muted-foreground">
                        {config.prompt.length} characters • {config.prompt.split("\n").length} lines
                      </div>
                      <div className="text-sm line-clamp-2 text-foreground font-mono">
                        {config.prompt}
                      </div>
                    </div>
                  </div>
                </Button>
              </div>
            )}

            {/* Template Variables */}
            {config.templateVariables &&
              Object.keys(config.templateVariables).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-gc-med text-foreground">
                    Template Variables
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                    {Object.entries(config.templateVariables).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className="flex items-center gap-2 text-sm">
                          <span className="font-mono text-muted-foreground">
                            {key}:
                          </span>
                          <span className="font-mono">
                            {JSON.stringify(value)}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error && !action) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <Card className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <div>
                  <h3 className="font-medium text-red-800 dark:text-red-200">Error Loading Action</h3>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <Button variant="outline" onClick={handleCancel}>
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Go Back
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!action) {
    return null;
  }

  const uiConfig = convertFromOpenAPIFormat(action?.action_config || {});

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge/agents")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Agents
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/agent/${agentId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {agent?.agent_name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">{action?.action_name}</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  {action?.action_name}
                </h1>
                {!isVersionView && (
                  <div className="flex items-center gap-2">
                    <VersionBadge 
                      version={latestVersion}
                      hasUnpublishedChanges={hasUnpublishedChanges}
                    />
                  </div>
                )}
                {isVersionView && version && (
                  <VersionBadge 
                    version={version}
                    hasUnpublishedChanges={false}
                  />
                )}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Manage action configuration, testing, and versioning
                </p>
                <div className="flex items-center gap-3">
                  {action?.action_type === "api_call" && (
                    <Button 
                      onClick={() => {
                        setTestModalOpen(true);
                        initializeTestParams(convertFromOpenAPIFormat(action?.action_config || {}) as APIActionConfig);
                      }} 
                      variant="outline" 
                      className="gap-2"
                    >
                      <PlayIcon className="w-4 h-4" />
                      Test
                    </Button>
                  )}
                  {!isVersionView && (hasUnpublishedChanges || !latestVersion) && (
                    <Button 
                      onClick={() => setPublishModalOpen(true)} 
                      size="sm"
                      className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
                    >
                      <ExternalLinkIcon className="w-4 h-4" />
                      Publish Version
                    </Button>
                  )}
                  {/* Show "View Latest" button when viewing an older version (not the latest) */}
                  {isVersionView && version && version !== latestVersion ? (
                    <Button
                      onClick={() => {
                        // Navigate to URL without version parameter (shows latest/draft)
                        navigate(`/agent/${agentId}/actions/${actionId}`);
                      }}
                      size="sm"
                      className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
                    >
                      View Latest
                    </Button>
                  ) : null}
                  {/* Show "Draft From v{latestVersion}" button when not viewing a version but latestVersion exists */}
                  {!isVersionView && latestVersion && !hasUnpublishedChanges ? (
                    <Button
                      onClick={() => {
                        if (latestVersion) {
                          handleDraftFromVersion(latestVersion);
                        }
                      }}
                      size="sm"
                      disabled={isDraftingFromVersion}
                      className="gap-2 bg-maitai-green hover:bg-maitai-green/90 text-white"
                    >
                      {isDraftingFromVersion ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        `Draft From v${latestVersion}`
                      )}
                    </Button>
                  ) : null}
                  {!isVersionView && (
                    <Button 
                      onClick={handleEdit} 
                      variant="outline" 
                      className="gap-2"
                      disabled={isDraftingFromVersion}
                    >
                      <EditIcon className="w-4 h-4" />
                      {isDraftingFromVersion ? "Creating Draft..." : "Edit Action"}
                    </Button>
                  )}
                  {isVersionView && version && (
                    <Button 
                      onClick={handleEdit} 
                      variant="outline" 
                      className="gap-2"
                      disabled={isDraftingFromVersion}
                    >
                      <EditIcon className="w-4 h-4" />
                      {isDraftingFromVersion ? "Creating Draft..." : "Edit Action"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab Navigation */}
        {(() => {
          const tabs: TabItem[] = [
            {
              key: "overview",
              label: "Overview",
              icon: <UserIcon className="w-4 h-4" />,
              content: (
                <div className="space-y-6">
                  {/* Action Details */}
                  <Card className="border-border">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-xl font-gc-med text-foreground">
                        Action Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Action Name */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-gc-med text-foreground">
                            Action Name
                          </h4>
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <span className="font-mono text-sm">{action.action_name}</span>
                          </div>
                        </div>

                        {/* Action Type */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-gc-med text-foreground">
                            Action Type
                          </h4>
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <Badge variant="outline">
                              {action.action_type === "api_call"
                                ? "API Call"
                                : action.action_type === "llm_call"
                                ? "LLM Call"
                                : action.action_type === "webhook_creation"
                                ? "Webhook Creation"
                                : action.action_type}
                            </Badge>
                          </div>
                        </div>

                        {/* Invocation Mode */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-gc-med text-foreground">
                            Invocation Mode
                          </h4>
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <Badge variant="outline">
                              {(action as any).invocation_mode === "background"
                                ? "Background"
                                : "Foreground"}
                            </Badge>
                          </div>
                        </div>

                        {/* Response Type (API actions only) */}
                        {action.action_type === "api_call" && (
                          <div className="space-y-2">
                            <h4 className="text-sm font-gc-med text-foreground">
                              Response Type
                            </h4>
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <Badge
                                variant="outline"
                                className={
                                  uiConfig.response_type === "mock"
                                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300"
                                    : "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                                }>
                                {uiConfig.response_type === "mock" ? "Mock" : "Real"}
                              </Badge>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Usage Guidance */}
                  <Card className="border-border">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-xl font-gc-med text-foreground">
                        Usage Guidance
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* What It Does */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-gc-med text-foreground">
                            What It Does
                          </h4>
                          <Tooltip
                            content="Define clearly what this action does, including inputs, outputs, and side effects so others know when to use it."
                            immediate>
                            <InfoIcon className="w-4 h-4 text-muted-foreground" />
                          </Tooltip>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg min-h-[48px]">
                          <p className="text-sm whitespace-pre-wrap">
                            {action.description || "No description provided"}
                          </p>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <h4 className="text-sm font-gc-med text-foreground">
                            When To Use
                          </h4>
                          <div className="p-3 bg-muted/50 rounded-lg min-h-[48px]">
                            <p className="text-sm whitespace-pre-wrap">
                              {(action as any)?.meta?.when_to_use || "No guidance provided"}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-sm font-gc-med text-foreground">
                            When Not To Use
                          </h4>
                          <div className="p-3 bg-muted/50 rounded-lg min-h-[48px]">
                            <p className="text-sm whitespace-pre-wrap">
                              {(action as any)?.meta?.when_not_to_use || "No guidance provided"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )
            },
            {
              key: "configuration",
              label: "Configuration",
              icon: <SettingsIcon className="w-4 h-4" />,
              content: (
                <div className="space-y-6">
                  {/* Type-Specific Configuration */}
                  {action.action_type === "api_call" ? (
                    renderAPIConfig(uiConfig as APIActionConfig)
                  ) : action.action_type === "llm_call" ? (
                    renderLLMConfig(uiConfig as LLMActionConfig)
                  ) : action.action_type === "webhook_creation" ? (
                    <Card className="border-border">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-lg font-gc-med text-foreground">
                          Webhook Configuration
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <h4 className="text-sm font-gc-med text-foreground">URL</h4>
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <span className="font-mono text-sm break-all">
                                {(uiConfig as any).url || "Not configured"}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <h4 className="text-sm font-gc-med text-foreground">
                              Method
                            </h4>
                            <div className="p-3 bg-muted/50 rounded-lg">
                              <Badge variant="outline">
                                {(uiConfig as any).method || "POST"}
                              </Badge>
                            </div>
                          </div>
                          {(uiConfig as any).timeout_ms && (
                            <div className="space-y-2">
                              <h4 className="text-sm font-gc-med text-foreground">
                                Timeout
                              </h4>
                              <div className="p-3 bg-muted/50 rounded-lg">
                                <span className="text-sm">
                                  {(uiConfig as any).timeout_ms / 1000} seconds
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="border-border">
                      <CardContent className="p-8">
                        <div className="text-center py-8 text-muted-foreground">
                          <p className="text-sm">
                            Unsupported action type: {action.action_type}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )
            },
            {
              key: "versions",
              label: "Versions",
              icon: <HistoryIcon className="w-4 h-4" />,
              content: (
                <AgentActionVersionsTable
                  actionId={Number(actionId)}
                  agentId={Number(agentId)}
                  apiKey={apiKey}
                  isVersionView={isVersionView}
                />
              )
            }
          ];

          return (
            <>
              {/* Tab Bar */}
              <Card className="border-border">
                <CardContent className="p-2">
                  <div className="flex gap-1">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-gc-med transition-colors",
                          activeTab === tab.key
                            ? "bg-maitai-green text-white"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        )}
                      >
                        {tab.icon}
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Tab Content */}
              {tabs.find((tab) => tab.key === activeTab)?.content}
            </>
          );
        })()}

        {/* Prompt Modal */}
        <PromptModal
          isOpen={promptModalOpen}
          onClose={() => setPromptModalOpen(false)}
          onSave={handlePromptSave}
          title="Prompt Template"
          description="Edit the LLM prompt template for this action. Use {{variable_name}} for dynamic values."
          initialValue={(action?.action_config as LLMActionConfig)?.prompt || ""}
          placeholder="Enter your prompt template here. Use {{variable_name}} for dynamic values."
          isSaving={promptSaving}
        />

        {/* Test Modal */}
        {testModalOpen && action?.action_type === "api_call" && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div>
                  <h3 className="text-xl font-gc-med text-foreground">Test API Action</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Test the "{action.action_name}" action with custom parameters
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setTestModalOpen(false);
                    setTestResponse({});
                    setTestParams({});
                    setTestRequestBody("");
                  }}
                  variant="ghost"
                  size="sm"
                  className="p-1"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="p-6 space-y-6">
                {(() => {
                  const config = convertFromOpenAPIFormat(action?.action_config || {}) as APIActionConfig;
                  const validationErrors = validateTestParams(config);
                
                const methodColors: { [key: string]: string } = {
                  GET: "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
                  POST: "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
                  PUT: "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300",
                  PATCH:
                    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
                  DELETE:
                    "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300",
                };

                return (
                  <>
                    {/* Endpoint Info */}
                    <div className="p-4 bg-muted/30 rounded-lg border space-y-3">
                      <div className="flex items-center gap-3">
                        {config.method && (
                          <Badge
                            className={`font-mono ${
                              methodColors[config.method] ||
                              "bg-gray-100 text-gray-800"
                            }`}>
                            {config.method}
                          </Badge>
                        )}
                        <span className="font-mono text-sm break-all flex-1">
                          {config.endpoint || "No endpoint configured"}
                        </span>
                      </div>

                      {/* Show resolved URL if we have implied base URL */}
                      {(config as any).implied_base_url && config.endpoint && (
                        <div className="pl-4 border-l-2 border-muted">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              Resolved URL:
                            </span>
                            <span className="font-mono text-sm break-all text-maitai-green">
                              {(config as any).implied_base_url.replace(/\/$/, "")}/{config.endpoint.replace(/^\//, "")}
                            </span>
                          </div>
                        </div>
                      )}

                      {(config.base_url ||
                        (config as any).implied_base_url) && (
                        <p className="text-xs text-muted-foreground">
                          Base URL:{" "}
                          <span className="font-mono">
                            {config.base_url ||
                              (config as any).implied_base_url}
                          </span>
                          {(config as any).implied_base_url &&
                            !config.base_url && (
                              <span className="text-muted-foreground/70">
                                {" "}
                                (inherited from agent)
                              </span>
                            )}
                        </p>
                      )}
                    </div>

                    {/* Parameters Section */}
                    {config.parameters && config.parameters.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="text-lg font-gc-med text-foreground">
                          Parameters
                        </h4>
                        <div className="grid gap-4">
                          {config.parameters.map((param, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-gc-med text-foreground">
                                  {param.name}
                                  {param.required && (
                                    <span className="text-red-500">*</span>
                                  )}
                                </Label>
                                <Badge variant="outline" className="text-xs">
                                  {param.in}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {param.type}
                                </Badge>
                              </div>
                              {param.type === "boolean" ? (
                                <Select
                                  value={
                                    testParams[param.name]?.toString() ||
                                    "false"
                                  }
                                  onValueChange={(value) =>
                                    setTestParams((prev) => ({
                                      ...prev,
                                      [param.name]: value === "true",
                                    }))
                                  }>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="true">true</SelectItem>
                                    <SelectItem value="false">false</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : param.enum && param.enum.length > 0 ? (
                                <Select
                                  value={testParams[param.name] || ""}
                                  onValueChange={(value) =>
                                    setTestParams((prev) => ({
                                      ...prev,
                                      [param.name]: value,
                                    }))
                                  }>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a value" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {param.enum.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  type={param.type === "number" || param.type === "integer" ? "number" : "text"}
                                  value={testParams[param.name] || ""}
                                  onChange={(e) => {
                                    const value = param.type === "number" || param.type === "integer" 
                                      ? parseFloat(e.target.value) || 0
                                      : e.target.value;
                                    setTestParams(prev => ({ ...prev, [param.name]: value }));
                                  }}
                                  placeholder={param.description || `Enter ${param.name}`}
                                  className={param.required && (!testParams[param.name] || testParams[param.name] === "") ? "border-red-500" : ""}
                                />
                              )}
                              {param.description && (
                                <p className="text-xs text-muted-foreground">
                                  {param.description}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Request Body Section */}
                    {config.method &&
                      ["POST", "PUT", "PATCH"].includes(config.method) && (
                        <div className="space-y-4">
                          <h4 className="text-lg font-gc-med text-foreground">
                            Request Body
                          </h4>
                          <Textarea
                            rows={8}
                            value={testRequestBody}
                            onChange={(e) => setTestRequestBody(e.target.value)}
                            placeholder='{\n  "key": "value"\n}'
                            className="font-mono text-sm"
                          />
                          {testRequestBody &&
                            testRequestBody.trim() &&
                            (() => {
                              try {
                                JSON.parse(testRequestBody);
                                return null;
                              } catch (_) {
                                return (
                                  <p className="text-xs text-red-500">
                                    Invalid JSON
                                  </p>
                                );
                              }
                            })()}
                        </div>
                      )}

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <h5 className="text-sm font-gc-med text-red-800 dark:text-red-200 mb-2">
                          Validation Errors:
                        </h5>
                        <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                          {validationErrors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Response Section */}
                    {(testResponse.status ||
                      testResponse.error ||
                      testResponse.loading) && (
                      <div className="space-y-4 border-t border-border pt-6">
                        <div className="flex items-center justify-between">
                          <h4 className="text-lg font-gc-med text-foreground">
                            Response
                          </h4>
                          {testResponse.status && (
                            <div className="flex items-center gap-2">
                              <Badge
                                className={`font-mono ${
                                  testResponse.status >= 200 &&
                                  testResponse.status < 300
                                    ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300"
                                    : testResponse.status >= 400 &&
                                      testResponse.status < 500
                                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300"
                                    : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300"
                                }`}>
                                {testResponse.status} {testResponse.statusText}
                              </Badge>
                              {testResponse.responseTime !== undefined && (
                                <Badge variant="outline" className="font-mono">
                                  {testResponse.responseTime}ms
                                </Badge>
                              )}
                              {testResponse.responseSize !== undefined && (
                                <Badge variant="outline" className="font-mono">
                                  {testResponse.responseSize < 1024
                                    ? `${testResponse.responseSize} B`
                                    : testResponse.responseSize < 1024 * 1024
                                    ? `${(
                                        testResponse.responseSize / 1024
                                      ).toFixed(1)} KB`
                                    : `${(
                                        testResponse.responseSize /
                                        (1024 * 1024)
                                      ).toFixed(1)} MB`}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>

                        {testResponse.loading && (
                          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                            <div className="w-4 h-4 border-2 border-maitai-green border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm text-muted-foreground">
                              Testing action...
                            </span>
                          </div>
                        )}

                        {testResponse.error && (
                          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <h5 className="text-sm font-gc-med text-red-800 dark:text-red-200 mb-2">
                              Error:
                            </h5>
                            <p className="text-sm text-red-700 dark:text-red-300">
                              {testResponse.error}
                            </p>
                          </div>
                        )}

                        {testResponse.status && testResponse.body && (
                          <div className="p-4 bg-muted/50 rounded-lg max-h-96 overflow-y-auto">
                            <pre className="text-sm font-mono whitespace-pre-wrap overflow-x-auto">
                              {JSON.stringify(testResponse.body, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="flex items-center justify-between p-6 border-t border-border">
              <div className="text-sm text-muted-foreground">
                {(() => {
                  const config = convertFromOpenAPIFormat(action?.action_config || {}) as APIActionConfig;
                  const errors = validateTestParams(config);
                  return errors.length > 0
                    ? `${errors.length} validation error(s)`
                    : "Ready to test";
                })()}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    const config = convertFromOpenAPIFormat(action?.action_config || {}) as APIActionConfig;
                    initializeTestParams(config);
                  }}>
                  Reset
                </Button>
                <Button
                  onClick={handleTestAction}
                  disabled={isTestingAction || validateTestParams(convertFromOpenAPIFormat(action?.action_config || {}) as APIActionConfig).length > 0}
                  className="gap-2"
                >
                  {isTestingAction ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-4 h-4" />
                      Run Test
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Modal */}
      {publishModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-xl font-gc-med text-foreground">Publish New Version</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Create a new published version of "{action?.action_name}"
                </p>
              </div>
              <Button
                onClick={() => {
                  setPublishModalOpen(false);
                  setPublishNotes("");
                  setPublishIncrement("patch");
                }}
                variant="ghost"
                size="sm"
                className="p-1"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Current version info */}
              <div className="p-4 bg-muted/30 rounded-lg border space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Current Version:</span>
                  <span className="font-mono text-sm font-gc-med">
                    {latestVersion || "No versions yet"}
                  </span>
                </div>
                {hasUnpublishedChanges && (
                  <p className="text-xs text-orange-600 dark:text-orange-400">
                    ⚠️ You have unpublished changes that will be included in this version
                  </p>
                )}
              </div>

              {/* Version increment selection */}
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">Version Increment</Label>
                <Select
                  value={publishIncrement}
                  onValueChange={(value) => setPublishIncrement(value as "major" | "minor" | "patch")}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="major">
                      <div className="flex flex-col items-start">
                        <span className="font-gc-med">Major</span>
                        <span className="text-xs text-muted-foreground">Breaking changes (e.g., 1.0.0 → 2.0.0)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="minor">
                      <div className="flex flex-col items-start">
                        <span className="font-gc-med">Minor</span>
                        <span className="text-xs text-muted-foreground">New features (e.g., 1.0.0 → 1.1.0)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="patch">
                      <div className="flex flex-col items-start">
                        <span className="font-gc-med">Patch</span>
                        <span className="text-xs text-muted-foreground">Bug fixes (e.g., 1.0.0 → 1.0.1)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Version notes */}
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">Version Notes (Optional)</Label>
                <Textarea
                  rows={4}
                  value={publishNotes}
                  onChange={(e) => setPublishNotes(e.target.value)}
                  placeholder="Describe what changed in this version..."
                  className="text-sm"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setPublishModalOpen(false);
                  setPublishNotes("");
                  setPublishIncrement("patch");
                }}
                disabled={isPublishing}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePublish}
                disabled={isPublishing}
                className="gap-2 bg-maitai-green hover:bg-maitai-green/90"
              >
                {isPublishing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <ExternalLinkIcon className="w-4 h-4" />
                    Publish
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Draft From Version Confirmation Dialog */}
      <AlertDialog 
        open={draftConfirmDialogOpen} 
        onOpenChange={(open) => {
          if (!open) {
            setDraftConfirmDialogOpen(false);
            setPendingDraftVersion(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-gc-med">
              Create Draft from Version
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to create a draft from version {pendingDraftVersion}? This will overwrite the current draft.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDraftFromVersion}
              className="bg-maitai-green hover:bg-maitai-green/90 text-white"
            >
              Create Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
    </>
  );
};

export default AgentActionOverview;
